import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    ReactNode,
    useRef,
} from 'react';
import axios from 'axios';
import { LanguageDto } from '../types';
import { updateUserProfile } from '../services/api.shop';

interface TranslationContextType {
    t: (key: string, params?: Record<string, unknown>) => string;
    changeLanguage: (lang: string, options?: { persist?: boolean }) => Promise<void>;
    currentLang: string;
    isLoading: boolean;
    languages: LanguageDto[];
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const translationCache: Record<string, Record<string, string>> = {};

/**
 * Critical UI strings before /api/translations loads (avoids raw keys like "state.loading").
 * API values override these once the dictionary is ready.
 */
const BUILTIN_FALLBACKS: Record<string, string> = {
    'state.loading': 'Loading',
};

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [currentLang, setCurrentLang] = useState(localStorage.getItem('language') || 'en');
    const [isLoading, setIsLoading] = useState(false);
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const initialized = useRef(false);
    const refreshInterval = useRef<ReturnType<typeof setInterval> | null>(null);

    // Latest dictionaries on refs so `t` can keep a stable identity.
    // Previously every provider re-render created a new `t`, and any useEffect that listed
    // `t` in its deps re-ran (sell-form edit load wiped in-progress category/specs).
    const languagesRef = useRef(languages);
    languagesRef.current = languages;
    const currentLangRef = useRef(currentLang);
    currentLangRef.current = currentLang;
    const translationsRef = useRef(translations);
    translationsRef.current = translations;

    const getTranslationKey = useCallback((langCode: string): string => {
        const lang = languagesRef.current.find((l) => l.code === langCode);
        return lang?.translationKey || langCode;
    }, []);

    /** Stable forever — always reads latest lang / dictionary via refs + module cache. */
    const t = useCallback((key: string, params: Record<string, unknown> = {}): string => {
        const translationKey = getTranslationKey(currentLangRef.current);
        const current =
            translationCache[translationKey] || translationsRef.current || {};
        let text = current[key] || BUILTIN_FALLBACKS[key] || key;
        Object.keys(params).forEach((param) => {
            text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), String(params[param] ?? ''));
        });
        return text;
    }, [getTranslationKey]);

    const loadLanguage = useCallback(
        async (lang: string, silent = false, persistLocal = true) => {
            const translationKey = getTranslationKey(lang);

            // Cache hit
            if (!silent && translationCache[translationKey]) {
                setTranslations(translationCache[translationKey]);
                setCurrentLang(lang);
                if (persistLocal) localStorage.setItem('language', lang);
                return;
            }

            if (!silent) setIsLoading(true);

            try {
                // Silent refresh must not reuse a stale module cache entry (admin edits).
                if (silent) {
                    delete translationCache[translationKey];
                }
                const { data } = await axios.get<Record<string, string>>(
                    `/api/translations/${translationKey}`
                );
                translationCache[translationKey] = data;

                if (!silent) {
                    setTranslations(data);
                    setCurrentLang(lang);
                    if (persistLocal) localStorage.setItem('language', lang);
                } else {
                    // Preload other langs into cache only; apply state only for active language refresh
                    const activeKey = getTranslationKey(currentLangRef.current);
                    if (translationKey === activeKey || lang === currentLangRef.current) {
                        setTranslations(data);
                    }
                }
            } catch {
                // keep previous strings if refresh fails
            } finally {
                if (!silent) setIsLoading(false);
            }
        },
        [getTranslationKey]
    );

    const fetchLanguagesAndReturn = useCallback(async (): Promise<LanguageDto[]> => {
        try {
            const { data } = await axios.get<LanguageDto[]>('/api/languages');

            const activeOnes = data.filter((l) => l.isActive);
            setLanguages(activeOnes);
            return activeOnes;
        } catch (err) {
            console.error('Failed to load languages', err);
            setLanguages([]);
            return [];
        }
    }, []);

    // Initial load + preload other languages
    useEffect(() => {
        if (initialized.current) return;

        const init = async () => {
            const activeLanguages = await fetchLanguagesAndReturn();
            const lang = currentLangRef.current;
            const hadStored = !!localStorage.getItem('language');
            await loadLanguage(lang, false, hadStored);

            activeLanguages
                .map((l) => l.code)
                .filter((code) => code !== lang)
                .forEach((code) => {
                    void loadLanguage(code, true);
                });

            initialized.current = true;
        };

        void init();
    }, [fetchLanguagesAndReturn, loadLanguage]);

    // === AUTO REFRESH: Every 10 minutes + when tab becomes visible ===
    useEffect(() => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
        }

        refreshInterval.current = setInterval(() => {
            void loadLanguage(currentLangRef.current, true);
        }, 10 * 60 * 1000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void loadLanguage(currentLangRef.current, true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (refreshInterval.current) clearInterval(refreshInterval.current);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentLang, loadLanguage]);

    const changeLanguage = useCallback(
        async (lang: string, options?: { persist?: boolean }) => {
            await loadLanguage(lang);
            if (!options?.persist) return;
            try {
                await updateUserProfile({ preferredLanguage: lang });
            } catch {
                // Profile save failed — localStorage preference from loadLanguage still applies
            }
        },
        [loadLanguage]
    );

    // Include `translations` so UI re-renders when the dictionary updates, while `t` stays
    // referentially stable (effects that only depend on `t` do not re-fire).
    const contextValue = useMemo(
        () => ({
            t,
            changeLanguage,
            currentLang,
            isLoading,
            languages,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- translations intentionally triggers consumer re-render only
        [t, changeLanguage, currentLang, isLoading, languages, translations]
    );

    return (
        <TranslationContext.Provider value={contextValue}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) throw new Error('useTranslation must be used within a TranslationProvider');
    return context;
};
